'use client';

import React, { useEffect, useState } from 'react';
import { MmMoneyIssuerDisplay } from '@vaiu/cash-token-card';
import {
  formatAmountForCurrency,
  isMmMoneyCurrency,
  parseMmMoneyIssuerId,
  type MmMoneyIssuerView,
} from '@vaiu/utils/mmMoney';
import { useRedeemUx } from './RedeemUxContext';

function useMmMoneyIssuerForCurrency(currencyKey: string): {
  issuer: Partial<MmMoneyIssuerView> | null;
  loading: boolean;
} {
  const { fetchMmMoneyIssuerProfile } = useRedeemUx();
  const [issuer, setIssuer] = useState<Partial<MmMoneyIssuerView> | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isMmMoneyCurrency(currencyKey)) {
      setIssuer(null);
      setLoading(false);
      return;
    }

    const issuerUserId = parseMmMoneyIssuerId(currencyKey);
    if (!issuerUserId) {
      setIssuer(null);
      setLoading(false);
      return;
    }

    if (!fetchMmMoneyIssuerProfile) {
      setIssuer({ issuerUserId });
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const profile = await fetchMmMoneyIssuerProfile(issuerUserId);
        if (cancelled) return;
        setIssuer(profile ? { ...profile, issuerUserId } : { issuerUserId });
      } catch {
        if (!cancelled) setIssuer({ issuerUserId });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [currencyKey, fetchMmMoneyIssuerProfile]);

  return { issuer, loading };
}

/** MM$ issuer row: icon + merchant / owner name (never the raw issuer user id). */
export function RedeemMmMoneyIssuerBanner({
  currencyKey,
  className = '',
}: {
  currencyKey: string;
  className?: string;
}) {
  const { issuer, loading } = useMmMoneyIssuerForCurrency(currencyKey);

  if (!isMmMoneyCurrency(currencyKey)) {
    return null;
  }

  const hasTitle = Boolean(
    issuer?.displayName?.trim() || issuer?.name?.trim() || issuer?.username?.trim()
  );

  return (
    <div
      className={`rounded-lg border bg-muted/30 px-3 py-2 ${className}`}
      aria-busy={loading}
    >
      <MmMoneyIssuerDisplay
        currencyKey={currencyKey}
        issuer={issuer ?? undefined}
        compact
        hideProfileAction
        omitIssuerIdFallback
        tickerOnly={!hasTitle && !issuer?.currencyIconUrl}
        className="min-w-0"
      />
    </div>
  );
}

/** Formatted monetary amount — MM$ uses ticker only, not the wire key. */
export function FormattedCurrencyAmount({
  amount,
  currency,
  className = '',
}: {
  amount: number;
  currency: string;
  className?: string;
}) {
  const { formatCurrency } = useRedeemUx();
  const [formatted, setFormatted] = useState(() => formatAmountForCurrency(amount, currency));

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const next = await formatCurrency(amount, currency);
        if (!cancelled) setFormatted(next);
      } catch {
        if (!cancelled) setFormatted(formatAmountForCurrency(amount, currency));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [amount, currency, formatCurrency]);

  return <span className={className}>{formatted}</span>;
}
